import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Briefcase, MapPin, Phone, Mail, DollarSign, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { key: 'auto', label: 'Auto & Mechanics' },
  { key: 'beauty', label: 'Hair & Beauty' },
  { key: 'wellness', label: 'Wellness & Massage' },
  { key: 'trade', label: 'Home & Trade' },
  { key: 'other', label: 'Other' },
];

const ServiceCreatePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useApp();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: '',
    location: '',
    contact_phone: '',
    contact_email: user?.email || '',
    price_info: '',
  });

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.category || !form.location) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          provider_id: user.id,
          provider_name: user.name,
        }),
      });
      if (res.ok) {
        toast.success('Service listed! Free for 7 days.');
        navigate('/services');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to list service');
      }
    } catch {
      toast.error('Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Layout title="List a Service" showBack>
        <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
          <Briefcase className="w-12 h-12 text-stone-300 mb-4" strokeWidth={1.5} />
          <h2 className="font-display text-xl text-stone-900 mb-2">Sign in to list your service</h2>
          <p className="text-stone-500 text-sm mb-6">Create an account to reach local customers</p>
          <Button onClick={() => navigate('/login')} className="bg-minted-900 hover:bg-minted-700 rounded-sm">
            Sign In
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="List a Service" showBack>
      {/* Hero */}
      <div className="bg-minted-900 text-white px-6 py-5">
        <p className="text-[10px] tracking-[0.25em] uppercase text-stone-400 mb-1">Services Directory</p>
        <h2 className="font-display text-lg font-medium tracking-tight">List Your Service</h2>
        <div className="mt-2 flex gap-2">
          <span className="text-[10px] bg-white/10 px-2 py-1 rounded-sm tracking-wider uppercase">Free for 7 days</span>
          <span className="text-[10px] bg-white/10 px-2 py-1 rounded-sm tracking-wider uppercase">Then $1/week</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="px-4 py-5 space-y-5">
        {/* Service Title */}
        <div>
          <Label className="text-[11px] tracking-wider uppercase text-stone-500 font-medium">Service Title *</Label>
          <Input
            placeholder="e.g. Mobile Mechanic, Hair Styling"
            value={form.title}
            onChange={(e) => updateField('title', e.target.value)}
            className="mt-1.5 rounded-sm border-stone-200 focus:border-minted-600 focus:ring-minted-600"
            data-testid="service-title-input"
          />
        </div>

        {/* Category */}
        <div>
          <Label className="text-[11px] tracking-wider uppercase text-stone-500 font-medium">Category *</Label>
          <div className="mt-1.5 grid grid-cols-2 gap-2">
            {categories.map((cat) => (
              <button
                key={cat.key}
                type="button"
                onClick={() => updateField('category', cat.key)}
                className={`px-3 py-2.5 rounded-sm text-xs font-medium transition-colors text-left ${
                  form.category === cat.key
                    ? 'bg-minted-900 text-white'
                    : 'bg-white text-stone-600 border border-stone-200 hover:border-stone-300'
                }`}
                data-testid={`category-${cat.key}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Description */}
        <div>
          <Label className="text-[11px] tracking-wider uppercase text-stone-500 font-medium">Description</Label>
          <Textarea
            placeholder="Describe your service, experience, and what you offer..."
            value={form.description}
            onChange={(e) => updateField('description', e.target.value)}
            className="mt-1.5 rounded-sm border-stone-200 min-h-[100px] focus:border-minted-600 focus:ring-minted-600"
            data-testid="service-description-input"
          />
        </div>

        {/* Location */}
        <div>
          <Label className="text-[11px] tracking-wider uppercase text-stone-500 font-medium">
            <MapPin className="w-3 h-3 inline mr-1" strokeWidth={1.5} />
            Location / Service Area *
          </Label>
          <Input
            placeholder="e.g. Sydney CBD, Northern Beaches"
            value={form.location}
            onChange={(e) => updateField('location', e.target.value)}
            className="mt-1.5 rounded-sm border-stone-200 focus:border-minted-600 focus:ring-minted-600"
            data-testid="service-location-input"
          />
        </div>

        {/* Contact */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] tracking-wider uppercase text-stone-500 font-medium">
              <Phone className="w-3 h-3 inline mr-1" strokeWidth={1.5} />
              Phone
            </Label>
            <Input
              type="tel"
              placeholder="04XX XXX XXX"
              value={form.contact_phone}
              onChange={(e) => updateField('contact_phone', e.target.value)}
              className="mt-1.5 rounded-sm border-stone-200 focus:border-minted-600 focus:ring-minted-600"
              data-testid="service-phone-input"
            />
          </div>
          <div>
            <Label className="text-[11px] tracking-wider uppercase text-stone-500 font-medium">
              <Mail className="w-3 h-3 inline mr-1" strokeWidth={1.5} />
              Email
            </Label>
            <Input
              type="email"
              placeholder="you@email.com"
              value={form.contact_email}
              onChange={(e) => updateField('contact_email', e.target.value)}
              className="mt-1.5 rounded-sm border-stone-200 focus:border-minted-600 focus:ring-minted-600"
              data-testid="service-email-input"
            />
          </div>
        </div>

        {/* Price Info */}
        <div>
          <Label className="text-[11px] tracking-wider uppercase text-stone-500 font-medium">
            <DollarSign className="w-3 h-3 inline mr-1" strokeWidth={1.5} />
            Pricing Info
          </Label>
          <Input
            placeholder="e.g. From $50/hr, Free quotes"
            value={form.price_info}
            onChange={(e) => updateField('price_info', e.target.value)}
            className="mt-1.5 rounded-sm border-stone-200 focus:border-minted-600 focus:ring-minted-600"
            data-testid="service-price-input"
          />
        </div>

        {/* Submit */}
        <div className="pt-2 pb-4">
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-minted-900 hover:bg-minted-700 rounded-sm h-12 text-sm tracking-wider uppercase"
            data-testid="submit-service-btn"
          >
            {loading ? 'Listing...' : 'List My Service'}
          </Button>
          <p className="text-center text-[11px] text-stone-400 mt-3">
            Your listing will be free for the first 7 days, then $1/week.
          </p>
        </div>
      </form>
    </Layout>
  );
};

export default ServiceCreatePage;
